import Debug from 'debug';
import { z } from 'zod';
const debug = Debug('whiteboard:assistant');
// Class to repeatedly extract a chunk of JSON from surrounding XML tags.
//
// Example:
//   <create>
//     {"key": "value"}
//   </create>

export type JsonData = Record<string, unknown> | unknown[];

export interface ParserResult {
  tagName: string;
  jsonData: JsonData;
}

export default class XMLWithEmbeddedJSONParser {
  private buffer: string;
  private currentTag: string | null;

  constructor() {
    this.buffer = '';
    this.currentTag = null;
  }

  append(chars: string): void {
    this.buffer += chars;
  }

  parseJSON(
    jsonContent: string,
    attemptingFix: boolean = false
  ): JsonData | null {
    try {
      const parsed = JSON.parse(jsonContent);
      const { data, success } = z
        .object({})
        .passthrough()
        .or(z.array(z.any()))
        .safeParse(parsed);
      return success ? data : null;
    } catch (e) {
      if (e instanceof SyntaxError) {
        // We recursively attempt to fix the JSON if it is malformed, but only once to avoid infinite loops.
        if (!attemptingFix) {
          jsonContent = jsonContent.trim();
          // Attempt to fix malformed JSON likely missing a starting or ending closing brace
          if (jsonContent.startsWith('{') && !jsonContent.endsWith('}')) {
            return this.parseJSON(`${jsonContent}}`, true);
          } else if (
            jsonContent.endsWith('}') &&
            !jsonContent.startsWith('{')
          ) {
            return this.parseJSON(`{${jsonContent}`, true);
          } else if (
            !jsonContent.startsWith('{') &&
            !jsonContent.endsWith('}')
          ) {
            return this.parseJSON(`{${jsonContent}}`, true);
          }
        }

        console.error('Invalid json found:', jsonContent, this.buffer);

        return null;
      } else {
        throw e;
      }
    }
  }

  // Parse the current buffer
  parse(): ParserResult | null {
    // Look for opening tag
    if (!this.currentTag) {
      const openTagMatch = this.buffer.match(
        /.*?<(create|update|delete|select|thinking)>/m
      );
      if (openTagMatch?.index !== undefined && openTagMatch[1]) {
        this.currentTag = openTagMatch[1];
        this.buffer = this.buffer.slice(
          openTagMatch.index + openTagMatch[0].length
        );
      }
    }

    // If we have a tag, look for closing tag
    if (this.currentTag) {
      const closingTag = `</${this.currentTag}>`;
      const closingIndex = this.buffer.indexOf(closingTag);

      if (closingIndex !== -1) {
        const jsonContent = this.buffer.slice(0, closingIndex);

        let result: ParserResult | null = null;

        if (this.currentTag === 'thinking') {
          debug('AI thought:', jsonContent);
          result = null;
        } else {
          const json = this.parseJSON(jsonContent);
          if (json) {
            result = {
              tagName: this.currentTag,
              jsonData: json,
            };
          }
        }

        // Reset the parser state
        this.buffer = this.buffer.slice(closingIndex + closingTag.length);
        this.currentTag = null;

        return result;
      }
    }

    return null;
  }
}
